/**
 * פורמט שורות טיסה למסך "פרטי חבילה" בצ'אט — קומפקטי, סדר קבוע: נתיב (אופציונלי) • תאריך • שעה • חברה+מספר.
 */
import {
  formatFlightNumberWithAirlineCode,
  resolveAirlineCodeFromPackageLeg,
  resolveAirlineNameFromPackageLeg,
} from "../Components/Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";

function cleanStr(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return "";
  return s;
}

function normalizeTimeHm(v) {
  const s = cleanStr(v);
  if (!s) return "";
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function normalizeIata3(v) {
  const s = cleanStr(v).toUpperCase().replace(/\s+/g, "");
  if (!s) return "";
  if (/^[A-Z]{2,3}$/.test(s)) return s;
  if (s.length >= 3 && /^[A-Z]+$/.test(s.slice(0, 3))) return s.slice(0, 3);
  return "";
}

function ymdToDdMmYyyy(ymd) {
  const s = cleanStr(ymd);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return "";
  return `${String(d).padStart(2, "0")}/${String(mo).padStart(2, "0")}/${y}`;
}

/** תאריך ושעת יציאה מרגל LDS גולמי כשחסרים בשדות ה-summary מהשרת */
function scheduleFromRawLeg(raw) {
  if (!raw || typeof raw !== "object") return { date: "", time: "" };
  const dep = cleanStr(raw.depDate);
  let date = "";
  let time = "";
  if (dep) {
    const [dPart, tPart] = dep.split(/\s+/);
    date = ymdToDdMmYyyy(dPart) || "";
    if (tPart) time = normalizeTimeHm(tPart);
  }
  if (!time) time = normalizeTimeHm(raw.depTime || raw.STD || raw.std);
  return { date, time };
}

function originDestFromRaw(rawLdsLeg) {
  if (!rawLdsLeg || typeof rawLdsLeg !== "object") return { o: "", d: "" };
  const leg = rawLdsLeg;
  const o =
    leg.depAirport?.code ||
    leg.from ||
    leg.From ||
    leg.departureAirport ||
    leg.origin ||
    (typeof leg.departure === "object" &&
      leg.departure &&
      (leg.departure.airportCode || leg.departure.code || leg.departure.airport));
  const d =
    leg.arrAirport?.code ||
    leg.to ||
    leg.To ||
    leg.arrivalAirport ||
    leg.destination ||
    (typeof leg.arrival === "object" &&
      leg.arrival &&
      (leg.arrival.airportCode || leg.arrival.code || leg.arrival.airport));
  return { o: normalizeIata3(o), d: normalizeIata3(d) };
}

function buildAirlineFlightTail(summaryLeg, rawLdsLeg, airlines) {
  const list = Array.isArray(airlines) ? airlines : [];
  let airlineName = "";
  if (rawLdsLeg) airlineName = cleanStr(resolveAirlineNameFromPackageLeg(rawLdsLeg, list));
  if (!airlineName) airlineName = cleanStr(summaryLeg?.airline);
  if (!airlineName) airlineName = cleanStr(summaryLeg?.airline_name_en);

  const airlineCode = rawLdsLeg
    ? resolveAirlineCodeFromPackageLeg(rawLdsLeg, list)
    : cleanStr(summaryLeg?.airline_iata) || null;

  const fnRaw =
    rawLdsLeg?.flightNumber != null && cleanStr(rawLdsLeg.flightNumber)
      ? cleanStr(rawLdsLeg.flightNumber)
      : cleanStr(summaryLeg?.flight_number);

  const fnNumeric = cleanStr(summaryLeg?.flight_number_numeric);

  const flightFmt = formatFlightNumberWithAirlineCode(airlineCode, fnRaw || null);

  if (airlineName) {
    const num = fnNumeric || (fnRaw ? String(fnRaw).replace(/\s+/g, "") : "");
    if (num) return `${airlineName} ${num}`.trim();
    return airlineName;
  }
  if (flightFmt) return flightFmt;
  if (fnRaw) return fnRaw;
  return "";
}

/**
 * מחרוזת עובדות בודדת (LTR) ללא תווית כיוון — מופרדת ב־•
 * @param {Record<string, unknown>|null|undefined} summaryLeg
 * @param {Record<string, unknown>|null|undefined} rawLdsLeg
 * @param {unknown[]} airlines
 * @returns {string|null}
 */
export function formatPackageDetailsFlightFacts(summaryLeg, rawLdsLeg, airlines) {
  const sum = summaryLeg && typeof summaryLeg === "object" ? summaryLeg : null;
  const parts = [];

  let o = sum ? normalizeIata3(sum.origin_iata) : "";
  let d = sum ? normalizeIata3(sum.destination_iata) : "";
  if (!o || !d) {
    const od = originDestFromRaw(rawLdsLeg);
    if (!o) o = od.o;
    if (!d) d = od.d;
  }
  if (o && d) parts.push(`${o} → ${d}`);

  let date = sum ? cleanStr(sum.date_display) : "";
  let time = sum ? normalizeTimeHm(sum.time_depart) : "";
  if (!date || !time) {
    const rawSch = scheduleFromRawLeg(rawLdsLeg);
    if (!date) date = rawSch.date;
    if (!time) time = rawSch.time;
  }
  if (date) parts.push(date);
  if (time) parts.push(time);

  const tail = buildAirlineFlightTail(sum || {}, rawLdsLeg, airlines);
  if (tail) parts.push(tail);

  if (parts.length === 0) return null;
  return parts.join(" • ");
}
